import { useEffect, useRef } from 'react';
import Lenis from 'lenis';

// Singleton Lenis instance shared across the app
let lenisInstance = null;

/**
 * Returns the current Lenis instance (if initialized)
 */
export const getLenis = () => lenisInstance;

/**
 * Scrolls to the top of the page using Lenis (or native fallback)
 */
export const lenisScrollToTop = () => {
    if (lenisInstance) {
        lenisInstance.scrollTo(0, { immediate: true });
    } else {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }
};

/**
 * useLenis — initializes a Lenis smooth scroll instance and drives it
 * with requestAnimationFrame. Should be called once at the App root.
 */
export const useLenis = () => {
    const rafRef = useRef(null);

    useEffect(() => {
        // Destroy any previous instance before creating a new one
        if (lenisInstance) {
            lenisInstance.destroy();
            lenisInstance = null;
        }

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
            infinite: false,
        });

        lenisInstance = lenis;

        // RAF loop — drives Lenis animation
        const raf = (time) => {
            lenis.raf(time);
            rafRef.current = requestAnimationFrame(raf);
        };

        rafRef.current = requestAnimationFrame(raf);

        return () => {
            // Cleanup on unmount
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            lenis.destroy();
            lenisInstance = null;
        };
    }, []);
};
